import FailedMultipartUploadException from '../domain/exceptions/FailedMultipartUploadException'
import { FILE_MAX_CHUNK_SIZE } from '../domain/models/FileMaxChunkSize'
import { FileUploadRepository } from '../domain/repositories/fileUploadRepository'
import { FileUploadDatasource } from './datasources/fileUploadDatasource'

export function fileUploadRepositoryAdapter(
  fileUploadDatasource: FileUploadDatasource
): FileUploadRepository {
  return {
    async uploadFile({
      file,
      generatedFileName,
      folderName,
      onUploadProgress,
      assignedUrls,
      uploadId,
      numberOfParts
    }) {
      const chunkSize =
        numberOfParts !== undefined
          ? Math.round(file.size / numberOfParts)
          : FILE_MAX_CHUNK_SIZE
      const partsCount = numberOfParts ?? Math.ceil(file.size / chunkSize)

      const fileParts: Blob[] = []
      for (let offset = 0; offset <= file.size; offset += chunkSize)
        fileParts.push(file.slice(offset, offset + chunkSize))

      let amountUploaded = 0
      const abortControllers: AbortController[] = []

      try {
        const uploadedParts = await Promise.all(
          fileParts.map(async (part, i) => {
            const ac = new AbortController()
            abortControllers.push(ac)

            const res = await fileUploadDatasource.uploadFile({
              file: new File([part], generatedFileName, {
                type: file.type
              }),
              url: assignedUrls[i],
              abortSignal: ac.signal
            })

            onUploadProgress?.({ loaded: ++amountUploaded, total: partsCount })
            return {
              partNumber: i + 1,
              etag: res.etag
            }
          })
        )

        await fileUploadDatasource.completeMultipartPresignUrl({
          fileName: generatedFileName,
          folderName,
          uploadId: uploadId,
          partETags: uploadedParts
        })

        const url = new URL(assignedUrls[0])
        return {
          url: `${url.origin}${url.pathname}`,
          fileName: generatedFileName
        }
      } catch {
        abortControllers.forEach((ac) => {
          ac.abort()
        })
        throw new FailedMultipartUploadException({
          action: 'Try again to upload.',
          message: 'An exception has ocurred while uploading a file part.'
        })
      }
    },

    async presignUrl(options) {
      return await fileUploadDatasource.presignUrl(options)
    }
  }
}
